/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useFetcher } from 'react-router';
import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useCheckoutContext } from '@/hooks/use-checkout';
import { useBasket, useBasketUpdater } from '@/providers/basket';
import type { ContactInfoData, PaymentData } from '@/lib/checkout/schemas';
import type { CheckoutActionData } from '@/components/checkout/types';
import type { ActionError } from '@/lib/error-codes';
import {
    CHECKOUT_STEPS,
    CHECKOUT_ACTION_INTENTS,
    type CheckoutStep,
} from '@/components/checkout/utils/checkout-context-types';
import { resourceRoutes } from '@/route-paths';

/** Persists create-account intent across reloads (mirrors handleCreateAccountPreferenceChange). */
const SESSION_SHOULD_CREATE_ACCOUNT = 'shouldCreateAccount';
/** Persists formatted contact phone when basket merge (e.g. OTP) drops it from the basket. */
const SESSION_CHECKOUT_CONTACT_PHONE = 'checkoutContactPhone';
/** Set by register-customer-selection while checkout registration OTP flow is active. */
const SESSION_REGISTERED_VIA_CHECKOUT = 'registeredViaCheckout';

/**
 * Action lifecycle states for tracking form submission progress.
 */
enum ActionState {
    /** No action has been submitted yet */
    NOT_STARTED = 'notStarted',
    /** Action was submitted, waiting for response */
    SUBMITTED = 'submitted',
    /** Response received, basket updated, waiting to exit edit mode */
    BASKET_UPDATED = 'basketUpdated',
    /** Edit mode exited, action complete */
    COMPLETED = 'completed',
}

/**
 * Tracks the current action submission lifecycle.
 * We must track the step here (not rely on editingStep from context) because
 * editingStep can change before action processing completes.
 */
type ActionLifecycle = {
    /** Which checkout step's action was submitted */
    step: CheckoutStep | null;
    /** Current state in the action lifecycle */
    state: ActionState;
};

/** Options passed when placing order (e.g. from payment form at time of Place Order click) */
export type PlaceOrderOptionsRef = MutableRefObject<{
    savePaymentToProfile?: boolean;
    useDifferentBilling?: boolean;
    /** Un-masked account number for Synchrony auth at place-order — never stored */
    ashleyAccountNumber?: string;
    /** APG canonical financing code (e.g. SYNC191 / GEN6003) for APG authorize fallback */
    ashleyApgCode?: string;
    /** Security code (CVV) for Synchrony auth at place-order — never stored (enableSynchronyCVV) */
    ashleyCvv?: string;
    /**
     * Synchrony promo disclosure HTML captured at term acceptance. Threaded to place-order so the
     * authorize call can stamp `paymentInstrument.custom.financeTermsAndConditions` without a
     * second server-side round-trip (which sometimes returns null).
     */
    ashleyDisclosureText?: string;
} | null>;

/** Single ref coordinating payment submission and place-order flow to avoid race conditions */
export type PaymentSubmissionRef = MutableRefObject<{
    formDataGetter: (() => PaymentData) | null;
    shouldPlaceOrderAfterPayment: boolean;
    options: {
        savePaymentToProfile?: boolean;
        useDifferentBilling?: boolean;
        /** Un-masked account number for Synchrony auth at place-order — never stored */
        ashleyAccountNumber?: string;
        /** APG canonical financing code (e.g. SYNC191 / GEN6003) for APG authorize fallback */
        ashleyApgCode?: string;
        /** Security code (CVV) for Synchrony auth at place-order — never stored (enableSynchronyCVV) */
        ashleyCvv?: string;
        /**
         * Synchrony promo disclosure HTML captured at term acceptance. Threaded to place-order so
         * the authorize call can stamp `paymentInstrument.custom.financeTermsAndConditions`
         * without a second server-side round-trip.
         */
        ashleyDisclosureText?: string;
    } | null;
    setFormErrors: ((errors: Record<string, { type: string; message: string }>) => void) | null;
}>;

/**
 * Custom hook for managing checkout form actions using React Router fetchers.
 *
 * This hook provides functionality to submit checkout form data for each step
 * using React Router's useFetcher for handling form submissions without navigation.
 * Each fetcher is keyed to maintain separate state for each checkout step.
 *
 * @param options.paymentSubmissionRef - Ref holding form data getter, place-order-after-payment flag, and options (preferred over placeOrderOptionsRef)
 * @param options.placeOrderOptionsRef - Optional ref for place-order options (legacy; use paymentSubmissionRef for new code)
 * @returns Object containing checkout action functions and fetcher states
 * @returns submitContactInfo - Function to submit contact information
 * @returns submitShippingAddress - Function to submit shipping address
 * @returns submitShippingOptions - Function to submit shipping options
 * @returns submitPayment - Function to submit payment information
 * @returns contactFetcher - React Router fetcher for contact info requests
 * @returns shippingAddressFetcher - React Router fetcher for shipping address requests
 * @returns shippingOptionsFetcher - React Router fetcher for shipping options requests
 * @returns paymentFetcher - React Router fetcher for payment requests
 * @returns isSubmitting - Function to check if a specific step is submitting
 */
/** When true, contact step should not advance (e.g. OTP modal is open or authorize in flight). */
export type OtpFlowActiveRef = MutableRefObject<boolean>;

/**
 * CHECKOUT-14 — Terms & Conditions acceptance captured at place-order time.
 * A ref (not a param) so BOTH place-order entry points — direct submit and the
 * post-payment effect — forward the same acceptance without re-plumbing args.
 */
export type TermsAcceptanceRef = MutableRefObject<{ accepted: boolean; version: string } | null>;

/** When true, shipping address step should not advance (no valid shipping methods for address). */
export type NoShippingMethodsRef = MutableRefObject<boolean>;

export function useCheckoutActions(options?: {
    paymentSubmissionRef?: PaymentSubmissionRef;
    placeOrderOptionsRef?: PlaceOrderOptionsRef;
    /** When .current is true, do not advance from contact step after submit (OTP modal flow). */
    otpFlowActiveRef?: OtpFlowActiveRef;
    /** CHECKOUT-14 — current Terms & Conditions acceptance, forwarded to the place-order action. */
    termsAcceptanceRef?: TermsAcceptanceRef;
    /** When .current is true, do not advance from shipping address step (no valid methods available). */
    noShippingMethodsRef?: NoShippingMethodsRef;
}) {
    const { exitEditMode, editingStep } = useCheckoutContext();
    const updateBasket = useBasketUpdater();
    const basket = useBasket();

    // Scope the payment + place-order fetcher keys to the current basket/order.
    // React Router stores keyed-fetcher data in the router (not component state),
    // so a fixed key retains its last settled `data` across unmounts and SPA
    // navigations. A successful place-order returns a redirect (which never
    // overwrites `fetcher.data`), so a prior FAILED attempt would otherwise
    // linger and re-surface on the NEXT order once a fresh checkout re-attaches
    // to the same key. Keying by basketId gives each order its own fetcher, so a
    // new order can never inherit a stale payment / place-order error.
    const orderKeySuffix = basket?.basketId ?? 'no-basket';

    const contactFetcher = useFetcher<CheckoutActionData>({ key: 'contact-form' });
    const shippingAddressFetcher = useFetcher<CheckoutActionData>({ key: 'shipping-address-form' });
    const shippingOptionsFetcher = useFetcher<CheckoutActionData>({ key: 'shipping-options-form' });
    const hdShippingTypeFetcher = useFetcher<CheckoutActionData>({ key: 'hd-shipping-type-form' });
    const combineDeliveriesFetcher = useFetcher<CheckoutActionData>({ key: 'combine-deliveries-form' });
    const deliveryScheduleFetcher = useFetcher<CheckoutActionData>({ key: 'delivery-schedule-form' });
    const paymentFetcher = useFetcher<CheckoutActionData>({ key: `payment-form-${orderKeySuffix}` });
    const placeOrderFetcher = useFetcher<{ success?: boolean; error?: ActionError; step?: string }>({
        key: `place-order-${orderKeySuffix}`,
    });

    // Track action submission lifecycle.
    // We track step here because editingStep from context can change before processing completes.
    const actionRef = useRef<ActionLifecycle>({ step: null, state: ActionState.NOT_STARTED });
    // Guard shipping-step auto-advance so ONLY the explicit "Continue to Payment"
    // path (submitHdShippingType(..., { advanceStep: true })) can exit edit mode.
    // Plain HD/THD radio toggles must never advance checkout by themselves.
    const hdShippingTypeAdvanceRequestedRef = useRef(false);

    // Restore create-account only when registration OTP flow is active — avoids stale shouldCreateAccount for returning shoppers
    const [shouldCreateAccount, setShouldCreateAccount] = useState(() => {
        if (typeof sessionStorage === 'undefined') {
            return false;
        }
        if (sessionStorage.getItem(SESSION_REGISTERED_VIA_CHECKOUT) !== 'true') {
            return false;
        }
        return sessionStorage.getItem(SESSION_SHOULD_CREATE_ACCOUNT) === 'true';
    });
    const [savedPaymentMethods, setSavedPaymentMethods] = useState(new Set<string>());

    // Stores the contact phone across basket mutations
    const contactPhoneRef = useRef<string | null>(null);

    // Restore phone from session when basket no longer has it after OTP / merge
    useEffect(() => {
        if (typeof sessionStorage === 'undefined') {
            return;
        }
        const stored = sessionStorage.getItem(SESSION_CHECKOUT_CONTACT_PHONE);
        if (stored && !contactPhoneRef.current) {
            contactPhoneRef.current = stored;
        }
    }, []);

    // If billing/shipping gains a phone later (e.g. payment step) and we have no ref/session yet, capture it for place order
    useEffect(() => {
        if (contactPhoneRef.current) {
            return;
        }
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_CHECKOUT_CONTACT_PHONE)) {
            return;
        }
        const fromBasket = basket?.billingAddress?.phone || basket?.shipments?.[0]?.shippingAddress?.phone;
        if (fromBasket) {
            contactPhoneRef.current = fromBasket;
        }
    }, [basket]);

    // Reset lifecycle when entering a new edit step
    useEffect(() => {
        if (editingStep !== null) {
            actionRef.current = { step: null, state: ActionState.NOT_STARTED };
        }
    }, [editingStep]);

    // Map checkout steps to their corresponding fetchers. INT-12 (Sprint N+1):
    // the delivery-schedule fetcher is intentionally NOT in this map — the
    // OHDS date popover wired inside <DeliveryMethodToggle/> (FE-08) is an
    // in-step PATCH like the HD shipping-type toggle, not a step submission,
    // so it never rides the SUBMITTED → BASKET_UPDATED → COMPLETED lifecycle.
    const fetcherMap: Partial<Record<CheckoutStep, typeof contactFetcher>> = {
        [CHECKOUT_STEPS.CONTACT_INFO]: contactFetcher,
        [CHECKOUT_STEPS.SHIPPING_ADDRESS]: shippingAddressFetcher,
        [CHECKOUT_STEPS.SHIPPING_OPTIONS]: shippingOptionsFetcher,
        [CHECKOUT_STEPS.PAYMENT]: paymentFetcher,
    };

    // Update basket context when action completes successfully
    // Updates client-side basket state from action responses. Root loader revalidation
    // only updates basketSnapshot, not the full basket. This ensures checkout step
    // progression (which depends on basket state) works correctly after form submissions.
    useEffect(() => {
        const { step, state } = actionRef.current;

        // Only process if we're in SUBMITTED state
        if (step === null || state !== ActionState.SUBMITTED) {
            return;
        }

        // CHECKOUT-10 — when the shopper clicks "Continue to Payment" from the
        // new <DeliveryGroups/>, we set actionRef.step = SHIPPING_OPTIONS and
        // submit through `hdShippingTypeFetcher` (NOT shippingOptionsFetcher)
        // because there is no OOTB shipping-method picker on this flow. Treat
        // the HD/THD fetcher as the confirmation fetcher for the shipping-
        // options step in that case.
        const fetcher =
            step === CHECKOUT_STEPS.SHIPPING_OPTIONS &&
            hdShippingTypeAdvanceRequestedRef.current &&
            hdShippingTypeFetcher.data?.success
                ? hdShippingTypeFetcher
                : fetcherMap[step];
        if (!fetcher?.data?.success || !fetcher.data.basket) {
            return;
        }

        // Transition: SUBMITTED -> BASKET_UPDATED
        actionRef.current = { step, state: ActionState.BASKET_UPDATED };
        updateBasket(fetcher.data.basket);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        contactFetcher.data,
        shippingAddressFetcher.data,
        shippingOptionsFetcher.data,
        hdShippingTypeFetcher.data,
        paymentFetcher.data,
    ]);

    // CHECKOUT-10 — Sync basket from the HD shipping-type fetcher.
    // This is intentionally outside the actionRef lifecycle: changing the HD
    // shipping type is an in-step toggle, not a step submission. We only push
    // the new basket (with refreshed c_hdFee / c_thdFee) into client state.
    useEffect(() => {
        const data = hdShippingTypeFetcher.data;
        if (data?.success && data.basket) {
            updateBasket(data.basket);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hdShippingTypeFetcher.data]);

    // CHECKOUT-16 — Sync basket from the combine-deliveries fetcher.
    // Like the HD shipping-type toggle, combining deliveries is an in-step
    // toggle (not a step submission): we push the re-enriched basket (with the
    // consolidated per-item c_deliveryMethod / c_combinedDeliveryEnabled and
    // recomputed fees) into client state so the delivery groups regroup.
    useEffect(() => {
        const data = combineDeliveriesFetcher.data;
        if (data?.success && data.basket) {
            updateBasket(data.basket);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [combineDeliveriesFetcher.data]);

    // INT-12 (Sprint N+1) — Sync basket from the delivery-schedule fetcher.
    // Same pattern as hdShippingTypeFetcher: the inline date picker is an
    // in-step PATCH that refreshes c_requestedDeliveryDatetime +
    // c_isUserRequestedDeliveryDatetime on the default shipment without
    // advancing the checkout step.
    useEffect(() => {
        const data = deliveryScheduleFetcher.data;
        if (data?.success && data.basket) {
            updateBasket(data.basket);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deliveryScheduleFetcher.data]);

    // Exit edit mode after basket has been updated
    // Depends on basket so it runs AFTER the basket state update has been applied
    useEffect(() => {
        const { step, state } = actionRef.current;

        // Only process if we're in BASKET_UPDATED state and in edit mode
        if (editingStep === null || step === null || state !== ActionState.BASKET_UPDATED) {
            return;
        }

        // CHECKOUT-10 — same fall-through as the SUBMITTED useEffect: when the
        // shipping-options step was confirmed via the new "Continue to Payment"
        // path, success lives on the HD/THD fetcher rather than the OOTB
        // shippingOptionsFetcher.
        const fetcher =
            step === CHECKOUT_STEPS.SHIPPING_OPTIONS &&
            hdShippingTypeAdvanceRequestedRef.current &&
            hdShippingTypeFetcher.data?.success
                ? hdShippingTypeFetcher
                : fetcherMap[step];
        if (!fetcher?.data?.success) {
            return;
        }

        // Do not advance from contact step when OTP modal is open or authorize is in flight
        if (step === CHECKOUT_STEPS.CONTACT_INFO && options?.otpFlowActiveRef?.current) {
            return;
        }

        // Do not advance from shipping-address step when the Experian validation hook flagged
        // the submitted address as needing shopper confirmation. The basket has already been
        // updated above (so the storefront can read c_experianValidationResult to render the
        // modal), but the step stays in edit mode until the resubmit with experianBypass=true
        // clears the pending flag. The actionRef is reset to SUBMITTED on the next submit, so
        // leaving it at BASKET_UPDATED here is safe.
        if (step === CHECKOUT_STEPS.SHIPPING_ADDRESS && fetcher.data?.requiresAddressConfirmation) {
            return;
        }

        // Do not advance from shipping address step when no valid shipping methods are available
        if (step === CHECKOUT_STEPS.SHIPPING_ADDRESS && options?.noShippingMethodsRef?.current) {
            return;
        }

        // Transition: BASKET_UPDATED -> COMPLETED
        actionRef.current = { step, state: ActionState.COMPLETED };
        hdShippingTypeAdvanceRequestedRef.current = false;
        exitEditMode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingStep, basket]);

    /**
     * Submits contact information to the contact info action.
     *
     * Email-only since phone and SMS consent moved to the shipping-address step.
     *
     * @param data - Contact form data containing only the shopper's email
     */
    const submitContactInfo = (data: ContactInfoData) => {
        // Prevent concurrent submissions
        if (contactFetcher.state === 'submitting') {
            return;
        }

        // Transition: IDLE -> SUBMITTED
        actionRef.current = { step: CHECKOUT_STEPS.CONTACT_INFO, state: ActionState.SUBMITTED };

        const formData = new FormData();
        formData.append('intent', CHECKOUT_ACTION_INTENTS.CONTACT_INFO);
        formData.append('email', data.email);

        void contactFetcher.submit(formData, {
            method: 'post',
        });
    };

    /**
     * Submits shipping address information to the shipping address action.
     *
     * @param formData - FormData containing shipping address fields
     */
    const submitShippingAddress = (formData: FormData) => {
        // Prevent concurrent submissions
        if (shippingAddressFetcher.state === 'submitting') {
            return;
        }

        // Transition: IDLE -> SUBMITTED
        actionRef.current = { step: CHECKOUT_STEPS.SHIPPING_ADDRESS, state: ActionState.SUBMITTED };

        // Add intent field
        formData.append('intent', CHECKOUT_ACTION_INTENTS.SHIPPING_ADDRESS);

        void shippingAddressFetcher.submit(formData, {
            method: 'post',
        });
    };

    /**
     * Submits shipping options to the shipping options action.
     *
     * @param formData - FormData containing selected shipping method
     */
    const submitShippingOptions = (formData: FormData) => {
        // Prevent concurrent submissions
        if (shippingOptionsFetcher.state === 'submitting') {
            return;
        }

        // Transition: IDLE -> SUBMITTED
        actionRef.current = { step: CHECKOUT_STEPS.SHIPPING_OPTIONS, state: ActionState.SUBMITTED };

        // Add intent field
        formData.append('intent', CHECKOUT_ACTION_INTENTS.SHIPPING_OPTIONS);

        void shippingOptionsFetcher.submit(formData, {
            method: 'post',
        });
    };

    /**
     * CHECKOUT-10 — Submits the HD shipping type ('HD' | 'THD') to the
     * `update-hd-shipping-type` action. PATCHes `basket.c_hdShippingType`
     * and triggers basket calculate. Uses a dedicated fetcher so the
     * shipping-options step's own fetcher state is not affected.
     *
     * In-step toggle by default — does not advance the checkout step. Pass
     * `{ advanceStep: true }` (used by the "Continue to Payment" button on
     * <DeliveryGroups/>) to ride the SUBMITTED → BASKET_UPDATED → COMPLETED
     * lifecycle and exit edit mode after the PATCH completes.
     *
     * @param value - 'HD' (White Glove) or 'THD' (Doorstep)
     * @param options - { advanceStep?: boolean }
     */
    const submitHdShippingType = (value: 'HD' | 'THD', { advanceStep = false }: { advanceStep?: boolean } = {}) => {
        if (hdShippingTypeFetcher.state === 'submitting') {
            return;
        }
        hdShippingTypeAdvanceRequestedRef.current = advanceStep;

        if (advanceStep) {
            // Transition: IDLE -> SUBMITTED. The SUBMITTED useEffect picks up
            // hdShippingTypeFetcher.data on success and advances to
            // BASKET_UPDATED → COMPLETED → exitEditMode().
            actionRef.current = { step: CHECKOUT_STEPS.SHIPPING_OPTIONS, state: ActionState.SUBMITTED };
        }

        const formData = new FormData();
        formData.append('intent', CHECKOUT_ACTION_INTENTS.UPDATE_HD_SHIPPING_TYPE);
        formData.append('value', value);
        if (advanceStep) {
            formData.append('confirmShippingOptions', 'true');
        }

        void hdShippingTypeFetcher.submit(formData, {
            method: 'post',
        });
    };

    /**
     * CHECKOUT-16 — Toggle "Combine my deliveries" on/off. Non-navigating,
     * in-step toggle (no step advance): PATCHes the persisted intent and the
     * combine-deliveries fetcher re-syncs the re-enriched basket so the
     * delivery groups regroup. Guarded against concurrent submits.
     *
     * @param enabled - true = consolidate CHD items onto HD; false = revert
     */
    const submitCombineDeliveries = (enabled: boolean) => {
        if (combineDeliveriesFetcher.state === 'submitting') {
            return;
        }

        const formData = new FormData();
        formData.append('intent', CHECKOUT_ACTION_INTENTS.COMBINE_DELIVERIES);
        formData.append('value', enabled ? 'on' : 'off');

        void combineDeliveriesFetcher.submit(formData, {
            method: 'post',
        });
    };

    /**
     * CHECKOUT-10 — Confirms the shipping-options step from the new
     * <DeliveryGroups/> "Continue to Payment" button. Re-PATCHes the current
     * `c_hdShippingType` (which also sets `c_shippingMethodConfirmed = true`
     * server-side) AND advances the checkout step state machine.
     *
     * Why re-PATCH the current value: idempotent, and guarantees
     * `c_shippingMethodConfirmed = true` even on baskets where the OOTB
     * `submit-shipping-address` action did not auto-confirm (e.g. when more
     * than one OOTB shipping method is applicable).
     *
     * @param currentValue - the current `c_hdShippingType` from the basket
     */
    const confirmDeliveryOptions = (currentValue: 'HD' | 'THD') => {
        submitHdShippingType(currentValue, { advanceStep: true });
    };

    /**
     * INT-12 (Sprint N+1) — Submits an OHDS delivery date. Called from
     * (a) the per-tile date popover inside <DeliveryMethodToggle/> (FE-08,
     * the production surface) and (b) the standalone <HdMethodCardDatePicker/>
     * (still mounted by its own Storybook stories). In-step PATCH (does NOT
     * advance the step lifecycle) — model identical to `submitHdShippingType`.
     * Pass `null` to clear the persisted date.
     *
     * @param selectedDate - ISO date (yyyy-MM-dd) or null to clear
     * @param options.isUserSelected - When `false`, the action sends an
     *   explicit `c_isUserRequestedDeliveryDatetime=false` so the backend
     *   hook preserves the SYSTEM-fallback contract even though a date IS
     *   being persisted (the inline picker uses this on first-render
     *   auto-populate). Default `true` mirrors a direct shopper pick.
     */
    const submitDeliverySchedule = (
        selectedDate: string | null,
        { isUserSelected = true }: { isUserSelected?: boolean } = {}
    ) => {
        if (deliveryScheduleFetcher.state === 'submitting') {
            return;
        }

        const formData = new FormData();
        formData.append('intent', CHECKOUT_ACTION_INTENTS.DELIVERY_SCHEDULE);
        formData.append('skipScheduling', selectedDate ? 'false' : 'true');
        if (selectedDate) {
            formData.append('selectedDate', selectedDate);
        }
        formData.append('isUserSelected', isUserSelected ? 'true' : 'false');

        void deliveryScheduleFetcher.submit(formData, {
            method: 'post',
        });
    };

    /**
     * Submits payment information to the payment action.
     *
     * @param data - Payment form data containing card details and billing info
     */
    const submitPayment = (data: PaymentData) => {
        // Prevent concurrent submissions
        if (paymentFetcher.state === 'submitting') {
            return;
        }

        // Transition: IDLE -> SUBMITTED
        actionRef.current = { step: CHECKOUT_STEPS.PAYMENT, state: ActionState.SUBMITTED };

        // The form binds to `billingSameAsShipping`; the server's
        // parsePaymentFromFormData reads the same key. `useDifferentBilling` is the
        // inverse and is kept on the form data for downstream consumers.
        const billingSameAsShipping = data.billingSameAsShipping === true;
        const useDifferentBilling = !billingSameAsShipping;

        // Convert typed data to FormData for fetcher submission
        const formData = new FormData();
        formData.append('intent', CHECKOUT_ACTION_INTENTS.PAYMENT);
        // Pass through paymentMethodId so the server can detect non-card methods (e.g. ACIMA_FINANCE).
        const paymentMethodId = (data as { paymentMethodId?: string }).paymentMethodId;
        if (paymentMethodId) formData.append('paymentMethodId', paymentMethodId);
        formData.append('cardNumber', data.cardNumber || '');
        formData.append('cardholderName', data.cardholderName || '');
        formData.append('expiryDate', data.expiryDate || '');
        formData.append('cvv', data.cvv || '');
        formData.append('billingSameAsShipping', String(billingSameAsShipping));
        formData.append('useDifferentBilling', String(useDifferentBilling));

        // Include saved payment method fields
        formData.append('useSavedPaymentMethod', data.useSavedPaymentMethod?.toString() || 'false');
        if (data.selectedSavedPaymentMethod) {
            formData.append('selectedSavedPaymentMethod', data.selectedSavedPaymentMethod);
        }

        // Ashley Advantage (Synchrony) financing — forward the type + accepted term so the server
        // attaches an ASHLEY_FINANCE instrument instead of treating this as a credit-card
        // submission. Without these the server's parsePaymentFromFormData never sees paymentType
        // and the place-order flow stalls.
        if (data.paymentType) formData.append('paymentType', data.paymentType);
        if (data.ashleyTermCode) formData.append('ashleyTermCode', data.ashleyTermCode);
        // APG canonical term code — required by APG's /authorizations endpoint (Genesis branch,
        // and Synchrony-with-v3-off fallback). See PaymentData docs.
        if (data.ashleyApgCode) formData.append('ashleyApgCode', data.ashleyApgCode);
        if (data.ashleyAccountNumber) formData.append('ashleyAccountNumber', data.ashleyAccountNumber);
        if (data.ashleyCvv) formData.append('ashleyCvv', data.ashleyCvv);
        if (data.ashleyCardType) formData.append('ashleyCardType', data.ashleyCardType);

        // Converge LightBox — the PAN/CVV are entered inside Converge's hosted iframe and never
        // reach our form; the callback returns an ssl_token plus card metadata. Forward those so
        // the server validates the token path (isLightBoxPayment branch) instead of demanding raw
        // card fields. Omitting them makes parsePaymentFromFormData see isLightBoxPayment=false and
        // fail validation on cardNumber/cardholderName/expiryDate/cvv.
        if (data.isLightBoxPayment) {
            formData.append('isLightBoxPayment', 'true');
            if (data.sslToken) formData.append('sslToken', data.sslToken);
            if (data.convergeCardType) formData.append('convergeCardType', data.convergeCardType);
            if (data.convergeCardHolder) formData.append('convergeCardHolder', data.convergeCardHolder);
            if (data.convergeExpirationMonth != null) {
                formData.append('convergeExpirationMonth', String(data.convergeExpirationMonth));
            }
            if (data.convergeExpirationYear != null) {
                formData.append('convergeExpirationYear', String(data.convergeExpirationYear));
            }
            // Masked PAN captured from the Converge onApproval `ssl_card_number` — used to
            // persist the last 4 in the order-review / confirmation UI (`c_lightBoxMaskedNumber`).
            if (data.convergeMaskedNumber) {
                formData.append('convergeMaskedNumber', data.convergeMaskedNumber);
            }
            // Session token + full transactioninfo blob — required so
            // ElavonAuthorizationHelper can write the three legacy-parity notes
            // (`Converge Session Token`, `Checkout JS Transaction`, `CardToken`)
            // on the placed order. Same short-lived-attr pipe as `sslToken`.
            if (data.convergeSessionToken) {
                formData.append('convergeSessionToken', data.convergeSessionToken);
            }
            if (data.convergeTransactionInfo) {
                formData.append('convergeTransactionInfo', data.convergeTransactionInfo);
            }
        }

        if (useDifferentBilling) {
            formData.append('billingFirstName', data.billingFirstName || '');
            formData.append('billingLastName', data.billingLastName || '');
            formData.append('billingAddress1', data.billingAddress1 || '');
            formData.append('billingAddress2', data.billingAddress2 || '');
            formData.append('billingCity', data.billingCity || '');
            formData.append('billingStateCode', data.billingStateCode || '');
            formData.append('billingPostalCode', data.billingPostalCode || '');
            formData.append('billingPhone', data.billingPhone || '');
            formData.append('billingCountryCode', data.billingCountryCode || 'US');
        }

        // Submit payment form data
        void paymentFetcher.submit(formData, {
            method: 'post',
        });
    };

    /**
     * Helper function to check if a specific checkout step is currently submitting.
     *
     * @param step - The checkout step to check ('contact' | 'shipping-address' | 'shipping-options' | 'payment')
     * @returns true if the step is currently submitting, false otherwise
     */
    const isSubmitting = (
        step: 'contact' | 'shipping-address' | 'shipping-options' | 'delivery-schedule' | 'payment'
    ): boolean => {
        switch (step) {
            case 'contact':
                return contactFetcher.state === 'submitting';
            case 'shipping-address':
                return shippingAddressFetcher.state === 'submitting';
            case 'shipping-options':
                return shippingOptionsFetcher.state === 'submitting';
            case 'delivery-schedule':
                return deliveryScheduleFetcher.state === 'submitting';
            case 'payment':
                return paymentFetcher.state === 'submitting';
            default:
                return false;
        }
    };

    /**
     * Callback for when payment methods are saved
     *
     * @param paymentId - The ID of the payment method that was saved
     */
    const handlePaymentMethodSaved = (paymentId: string) => {
        setSavedPaymentMethods((prev) => new Set([...prev, paymentId]));
    };

    /**
     * Callback for when create account preference changes.
     * Memoized so consumers (e.g. useEffect deps in checkout-form-page) don't fire on every render.
     */
    const handleCreateAccountPreferenceChange = useCallback((shouldCreate: boolean) => {
        setShouldCreateAccount(shouldCreate);

        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(SESSION_SHOULD_CREATE_ACCOUNT, shouldCreate.toString());
        }
    }, []);

    /**
     * Submits the place-order action via fetcher so errors can be shown in-page.
     */
    const submitPlaceOrder = () => {
        if (placeOrderFetcher.state === 'submitting') {
            return;
        }
        const formData = new FormData();
        formData.append('shouldCreateAccount', shouldCreateAccount ? 'true' : 'false');
        const registrationFlowActive =
            typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_REGISTERED_VIA_CHECKOUT) === 'true';
        formData.append('checkoutRegistrationIntent', registrationFlowActive ? 'true' : 'false');
        const placeOrderOpts =
            options?.paymentSubmissionRef?.current?.options ?? options?.placeOrderOptionsRef?.current;
        if (placeOrderOpts?.savePaymentToProfile) {
            formData.append('savePaymentToProfile', 'true');
        }
        if (typeof placeOrderOpts?.useDifferentBilling === 'boolean') {
            formData.append('useDifferentBilling', String(placeOrderOpts.useDifferentBilling));
        }
        // Pass un-masked Ashley Advantage account number for Synchrony auth (cleared after use)
        if (placeOrderOpts?.ashleyAccountNumber) {
            formData.append('ashleyAccountNumber', placeOrderOpts.ashleyAccountNumber);
        }
        if (placeOrderOpts?.ashleyApgCode) {
            formData.append('ashleyApgCode', placeOrderOpts.ashleyApgCode);
        }
        // Pass the security code (CVV) for Synchrony auth when enableSynchronyCVV is on (never stored)
        if (placeOrderOpts?.ashleyCvv) {
            formData.append('ashleyCvv', placeOrderOpts.ashleyCvv);
        }
        // Forward the Synchrony promo disclosure HTML captured at term acceptance so the authorize
        // call can stamp it onto the order (`paymentInstrument.custom.financeTermsAndConditions`).
        if (placeOrderOpts?.ashleyDisclosureText) {
            formData.append('ashleyDisclosureText', placeOrderOpts.ashleyDisclosureText);
        }
        // Pass the contact phone captured at submission time (session + ref survive basket merge / reload)
        const storedPhone =
            typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(SESSION_CHECKOUT_CONTACT_PHONE) : null;
        const contactPhone =
            contactPhoneRef.current ||
            storedPhone ||
            basket?.billingAddress?.phone ||
            basket?.shipments?.[0]?.shippingAddress?.phone;
        if (contactPhone) {
            formData.append('contactPhone', contactPhone);
        }
        // CHECKOUT-14 — forward Terms & Conditions acceptance (+ version) for
        // server-side enforcement. The server rejects placement when acceptance
        // is absent, so this must ride on every place-order submission.
        const termsAcceptance = options?.termsAcceptanceRef?.current;
        if (termsAcceptance?.accepted) {
            formData.append('termsAccepted', 'true');
            formData.append('termsAcceptedVersion', termsAcceptance.version);
        }
        void placeOrderFetcher.submit(formData, {
            method: 'post',
            action: resourceRoutes.placeOrder,
        });
    };

    return {
        // Action functions
        submitContactInfo,
        submitShippingAddress,
        submitShippingOptions,
        submitHdShippingType,
        submitCombineDeliveries,
        confirmDeliveryOptions,
        submitDeliverySchedule,
        submitPayment,
        submitPlaceOrder,

        // Fetcher objects
        contactFetcher,
        shippingAddressFetcher,
        shippingOptionsFetcher,
        hdShippingTypeFetcher,
        combineDeliveriesFetcher,
        deliveryScheduleFetcher,
        paymentFetcher,
        placeOrderFetcher,

        // Helper functions
        isSubmitting,

        // Create account state and functions
        shouldCreateAccount,
        savedPaymentMethods,
        handlePaymentMethodSaved,
        handleCreateAccountPreferenceChange,
    };
}
